// HireDevelopers.js

import React, { useEffect } from "react";
import { Container, Box, Typography, Grid, Button, useTheme, Chip } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

import PeopleIcon from "@mui/icons-material/People";
import DeveloperModeIcon from "@mui/icons-material/DeveloperMode";
import SpeedIcon from "@mui/icons-material/Speed";
import PublicIcon from "@mui/icons-material/Public";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

export default function HireDevelopers() {
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const DARK_NAVY = "#0A192F";
  const LIGHT_GRAY = "#B0BEC5";
  const PRIMARY_GREEN = theme.palette.success.main;

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const roles = [
    "Flutter Developers",
    "React Developers",
    "Full‑Stack Engineers",
    "Backend Engineers",
    "Cloud & DevOps Engineers",
    "QA Automation Engineers",
    "UI/UX Designers",
  ];

  const highlights = [
    {
      icon: <WorkspacePremiumIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Senior‑level Expertise",
      description:
        "Industry‑experienced professionals with strong fundamentals, clean coding practices, and real product exposure.",
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Fast Onboarding",
      description:
        "Quick matching, smooth handover, and onboarding support so your projects keep moving without delays.",
    },
    {
      icon: <PublicIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Global Engagement",
      description:
        "Deploy talent for India, US, UK, and other markets — remote or onsite, full‑time or part‑time.",
    },
    {
      icon: <DeveloperModeIcon sx={{ fontSize: 42, color: PRIMARY_GREEN }} />,
      title: "Product‑First Thinking",
      description:
        "Developers who understand product, UX, and long‑term maintainability — not just task completion.",
    },
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 8, md: 12 },
        backgroundColor: DARK_NAVY,
        color: "white",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <motion.div variants={containerVariants} initial="hidden" animate="visible">
          <Grid container spacing={6} alignItems="flex-start">
            {/* LEFT CONTENT */}
            <Grid item xs={12} md={6}>
              <motion.div variants={itemVariants}>
                <Typography
                  variant="h3"
                  fontWeight={700}
                  sx={{
                    fontSize: { xs: "2.3rem", sm: "3rem", md: "3.3rem" },
                    mb: 2,
                    lineHeight: 1.2,
                  }}
                >
                  Hire <Box component="span" sx={{ color: PRIMARY_GREEN }}>Developers</Box> you can trust.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="h6" sx={{ color: LIGHT_GRAY, fontWeight: 300, mb: 3 }}>
                  Get access to vetted, senior‑level engineers and designers backed by Arocoiris
                  Technology’s product, architecture, and delivery experience.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography variant="body1" sx={{ color: LIGHT_GRAY, mb: 2, fontWeight: 300 }}>
                  Whether you need a single engineer or a complete pod, we provide flexible engagement
                  models — contract, dedicated monthly, or hybrid — for both Indian and international clients.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants} style={{ marginTop: theme.spacing(2), marginBottom: theme.spacing(3) }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: LIGHT_GRAY }}>
                  Roles we can deploy:
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {roles.map((role) => (
                    <Chip
                      key={role}
                      label={role}
                      variant="outlined"
                      color="success"
                      size="small"
                      sx={{
                        borderRadius: 2,
                        borderColor: "rgba(148, 163, 184, 0.4)",
                        color: LIGHT_GRAY,
                      }}
                    />
                  ))}
                </Box>
              </motion.div>

              <motion.div
                variants={itemVariants}
                style={{ display: "flex", gap: theme.spacing(2), flexWrap: "wrap" }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/contact")}
                >
                  Tell Us Your Requirement
                </Button>

                <Button
                  variant="outlined"
                  color="success"
                  size="large"
                  sx={{ px: 5, py: 1.5, fontWeight: 700, borderRadius: 2 }}
                  onClick={() => navigate("/services")}
                >
                  View All Services
                </Button>
              </motion.div>
            </Grid>

            {/* RIGHT CONTENT: HIGHLIGHTS */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {highlights.map((item) => (
                  <Grid item xs={12} sm={6} key={item.title}>
                    <motion.div
                      variants={itemVariants}
                      style={{
                        background: "rgba(15, 23, 42, 0.9)",
                        borderRadius: 16,
                        padding: "18px 16px",
                        border: "1px solid rgba(148, 163, 184, 0.25)",
                        height: "100%",
                      }}
                    >
                      {item.icon}
                      <Typography variant="subtitle1" sx={{ mt: 1, mb: 0.5, fontWeight: 600 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: LIGHT_GRAY, fontSize: 13 }}>
                        {item.description}
                      </Typography>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}